import { Html5Qrcode } from 'html5-qrcode';
import { RefObject, useCallback, useEffect, useRef, useState } from 'react';

export type CapabilitiesLike = MediaTrackCapabilities & {
  zoom?: { min: number; max: number; step: number };
  torch?: boolean;
  focusDistance?: { min: number; max: number; step?: number };
};

export type ScannerTuning = {
  fps?: number;
  qrboxPct?: number;
  resolution?: { width: number; height: number };
  continuousFocus?: boolean;
  useBarcodeDetector?: boolean;
  focusDistance?: number | null;
  zoomStorageKeyPrefix?: string;
};

export const ZOOM_STORAGE_PREFIX = 'tags:qr:zoom:';

const DEFAULT_ZOOM = 3;

export const findRearCamera = (devices: { id: string; label: string }[]): { id: string; label: string } | undefined =>
  devices.find((d) => /back|rear|environment/i.test(d.label)) ?? devices[devices.length - 1];

type UseQrScannerOptions = {
  readerId: string;
  onDecode: (decodedText: string) => void;
  tuning?: ScannerTuning;
};

type UseQrScannerResult = {
  start: (cameraId: string) => Promise<void>;
  stop: () => Promise<void>;
  running: boolean;
  error: string | null;
  capabilities: CapabilitiesLike | null;
  zoom: number | null;
  setZoom: (zoom: number) => void;
  torch: boolean;
  setTorch: (on: boolean) => void;
  readerBoxRef: RefObject<HTMLDivElement | null>;
};

export const useQrScanner = ({ readerId, onDecode, tuning }: UseQrScannerOptions): UseQrScannerResult => {
  const {
    fps = 8,
    qrboxPct = 55,
    continuousFocus = true,
    useBarcodeDetector = true,
    focusDistance = null,
    zoomStorageKeyPrefix = ZOOM_STORAGE_PREFIX,
  } = tuning ?? {};
  const { width: resolutionWidth = 3840, height: resolutionHeight = 2160 } = tuning?.resolution ?? {};

  const instRef = useRef<Html5Qrcode | null>(null);
  const generationRef = useRef(0);
  const readerBoxRef = useRef<HTMLDivElement | null>(null);
  const capabilitiesRef = useRef<CapabilitiesLike | null>(null);
  const zoomRef = useRef<number | null>(null);
  const onDecodeRef = useRef(onDecode);
  onDecodeRef.current = onDecode;

  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [capabilities, setCapabilities] = useState<CapabilitiesLike | null>(null);
  const [zoom, setZoom] = useState<number | null>(null);
  const [torch, setTorch] = useState(false);
  const [activeCameraId, setActiveCameraId] = useState('');

  const stopInstance = async () => {
    const inst = instRef.current;
    if (!inst) return;
    try {
      await inst.stop();
    } catch {
      /* noop */
    }
    try {
      await inst.clear();
    } catch {
      /* noop */
    }
  };

  const stop = useCallback(async () => {
    generationRef.current += 1;
    setRunning(false);
    await stopInstance();
  }, []);

  const start = useCallback(
    async (cameraId: string) => {
      const generation = ++generationRef.current;
      setActiveCameraId(cameraId);
      setError(null);
      setRunning(false);
      setCapabilities(null);
      setZoom(null);
      setTorch(false);

      await stopInstance();
      if (generation !== generationRef.current) return;

      const inst = new Html5Qrcode(readerId, {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        experimentalFeatures: { useBarCodeDetectorIfSupported: useBarcodeDetector } as any,
        verbose: false,
      });
      instRef.current = inst;

      const advanced: MediaTrackConstraintSet[] = [];
      if (continuousFocus) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        advanced.push({ focusMode: 'continuous' } as any);
      }

      const config = {
        fps,
        qrbox: (vw: number, vh: number) => {
          const min = Math.floor(Math.min(vw, vh) * (qrboxPct / 100));
          return { width: min, height: min };
        },
        videoConstraints: {
          deviceId: { exact: cameraId },
          width: { ideal: resolutionWidth },
          height: { ideal: resolutionHeight },
          frameRate: { ideal: 30 },
          ...(advanced.length > 0 ? { advanced } : {}),
        },
      };

      try {
        await inst.start(
          { deviceId: { exact: cameraId } },
          config,
          (decodedText: string) => {
            if (generation !== generationRef.current) return;
            onDecodeRef.current(decodedText);
          },
          () => {
            // per-frame decode failures are normal; ignore
          },
        );
        if (generation !== generationRef.current) {
          try {
            await inst.stop();
          } catch {
            /* noop */
          }
          try {
            await inst.clear();
          } catch {
            /* noop */
          }
          return;
        }
        setRunning(true);
        try {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const caps = (inst as any).getRunningTrackCapabilities?.() as CapabilitiesLike | undefined;
          setCapabilities(caps ?? null);
          if (caps?.zoom) {
            // Prefer 3x as the zero-config default, clamped to the camera's range.
            let initialZoom = Math.min(caps.zoom.max, Math.max(caps.zoom.min, DEFAULT_ZOOM));
            try {
              const stored = localStorage.getItem(`${zoomStorageKeyPrefix}${cameraId}`);
              if (stored !== null) {
                const parsed = Number(stored);
                if (Number.isFinite(parsed)) {
                  initialZoom = Math.min(caps.zoom.max, Math.max(caps.zoom.min, parsed));
                }
              }
            } catch {
              /* localStorage unavailable */
            }
            setZoom(initialZoom);
          }
        } catch {
          setCapabilities(null);
        }
      } catch (e) {
        if (generation === generationRef.current) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          setError((e as any)?.message ?? String(e));
          setRunning(false);
        }
        throw e;
      }
    },
    [readerId, fps, qrboxPct, resolutionWidth, resolutionHeight, continuousFocus, useBarcodeDetector, zoomStorageKeyPrefix],
  );

  // Apply zoom/torch/focusDistance to the running track.
  useEffect(() => {
    const inst = instRef.current;
    if (!inst || !running) return;
    const advanced: MediaTrackConstraintSet[] = [];
    if (zoom !== null) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      advanced.push({ zoom } as any);
    }
    if (capabilities?.torch !== undefined) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      advanced.push({ torch } as any);
    }
    if (focusDistance !== null && capabilities?.focusDistance) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      advanced.push({ focusDistance } as any);
    }
    if (advanced.length === 0) return;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const apply = (inst as any).applyVideoConstraints?.({ advanced }) as Promise<unknown> | undefined;
    apply?.catch(() => {
      /* noop */
    });
  }, [zoom, torch, focusDistance, capabilities, running]);

  // Persist last zoom per camera (debounced).
  useEffect(() => {
    if (!activeCameraId || zoom === null) return undefined;
    const t = setTimeout(() => {
      try {
        localStorage.setItem(`${zoomStorageKeyPrefix}${activeCameraId}`, String(zoom));
      } catch {
        /* localStorage unavailable */
      }
    }, 200);
    return () => clearTimeout(t);
  }, [activeCameraId, zoom, zoomStorageKeyPrefix]);

  // Mirror capabilities + zoom into refs so the touch handlers can read current values without re-attaching.
  useEffect(() => {
    capabilitiesRef.current = capabilities;
  }, [capabilities]);
  useEffect(() => {
    zoomRef.current = zoom;
  }, [zoom]);

  // Pinch-to-zoom on the video container. Re-attach when a scan session starts so the
  // element is guaranteed to be mounted.
  useEffect(() => {
    const el = readerBoxRef.current;
    if (!el) return undefined;
    let pinching = false;
    let initialDist = 0;
    let initialZoom = 0;

    const distance = (touches: TouchList) => {
      const dx = touches[0].clientX - touches[1].clientX;
      const dy = touches[0].clientY - touches[1].clientY;
      return Math.hypot(dx, dy);
    };

    const onStart = (e: TouchEvent) => {
      if (e.touches.length !== 2) return;
      const caps = capabilitiesRef.current;
      if (!caps?.zoom) return;
      pinching = true;
      initialDist = distance(e.touches);
      initialZoom = zoomRef.current ?? caps.zoom.min;
      e.preventDefault();
    };

    const onMove = (e: TouchEvent) => {
      if (!pinching || e.touches.length !== 2) return;
      const caps = capabilitiesRef.current;
      if (!caps?.zoom || initialDist === 0) return;
      const d = distance(e.touches);
      const ratio = d / initialDist;
      const next = Math.min(caps.zoom.max, Math.max(caps.zoom.min, initialZoom * ratio));
      setZoom(next);
      e.preventDefault();
    };

    const onEnd = () => {
      pinching = false;
    };

    el.addEventListener('touchstart', onStart, { passive: false });
    el.addEventListener('touchmove', onMove, { passive: false });
    el.addEventListener('touchend', onEnd);
    el.addEventListener('touchcancel', onEnd);
    return () => {
      el.removeEventListener('touchstart', onStart);
      el.removeEventListener('touchmove', onMove);
      el.removeEventListener('touchend', onEnd);
      el.removeEventListener('touchcancel', onEnd);
    };
  }, [running]);

  // Stop the camera when the consumer unmounts. html5-qrcode's stop() THROWS
  // synchronously when the scanner is not running (e.g. it was already stopped
  // by a decode), so the whole call must sit inside try/catch — a .catch() on
  // the promise alone lets the synchronous throw crash React's unmount commit.
  useEffect(() => {
    return () => {
      generationRef.current += 1;
      const inst = instRef.current;
      if (!inst) return;
      try {
        inst
          .stop()
          .catch(() => {
            /* noop */
          })
          .finally(() => {
            try {
              inst.clear();
            } catch {
              /* noop */
            }
          });
      } catch {
        try {
          inst.clear();
        } catch {
          /* noop */
        }
      }
    };
  }, []);

  return { start, stop, running, error, capabilities, zoom, setZoom, torch, setTorch, readerBoxRef };
};

export default useQrScanner;
